import {
    PrinterOutlined,
    UserOutlined,
    DesktopOutlined,
    DollarOutlined,
    GoldOutlined,
    FileTextOutlined,
    StarOutlined,
    SettingOutlined,
    FileProtectOutlined,
    KeyOutlined,
    GlobalOutlined,
    BellOutlined,
    ShopOutlined,
    RobotOutlined,
    AppstoreAddOutlined,
    SafetyOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Descriptions,
    Divider,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Switch,
    Tabs,
    Typography,
    message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector';
import MetalPriceConfig from '../components/MetalPriceConfig';
import UserCategoryPermissions from '../components/UserCategoryPermissions';
import UserManagement from '../components/UserManagement';
import HardwareInstallations from '../components/HardwareInstallations';
import CashPointStations from '../components/CashPointStations';
import ApplicationModules from '../components/ApplicationModules';
import CashPointAlertSettings from '../components/CashPointAlertSettings';
import LabelPrinterSettings from '../components/LabelPrinterSettings';
import ScaleTestCard from '../components/ScaleTestCard';
import LoyaltySettings from '../components/LoyaltySettings';
import AiAssistantSettings from '../components/AiAssistantSettings';
import BusinessTimeZoneSettings from '../components/BusinessTimeZoneSettings';
import ZeroCommissionPromotionsSettings from '../components/ZeroCommissionPromotionsSettings';
import PageHeader from '../components/PageHeader';
import ContractDocumentEditor from '../components/ContractDocumentEditor';
import InvoiceDocumentEditor from '../components/InvoiceDocumentEditor';
import { type InvoiceDocumentTemplatePayload, useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
    CONFIGURATION_TAB_QUERY_PARAM,
    resolveConfigurationTab,
} from '../domain/configurationTabs';
import { parseDelphiSettingNumber, toDelphiSettingNumber } from '../domain/delphiSettingNumber';
import { type ContractDocumentEngine, type InvoiceDocumentEngine } from '../domain/documentEngines';
import { appRelease } from '../domain/appRelease';
import {
    describeIdleTimeout,
    idleTimeoutOptions,
    resolveSessionIdleTimeoutMinutes,
    SESSION_IDLE_TIMEOUT_CONFIG_KEY,
} from '../domain/sessionActivity';
import { ThemeName, useTheme } from '../theme';
import { themeSelectOptions } from '../theme/themeOptions';

const { Title } = Typography;
const { Option } = Select;

type FormValues = Record<string, string | number | boolean | undefined>;

const { version: appVersion, lastUpdate: lastProgramUpdate } = appRelease;

const ConfigurePage = () => {
    const { t } = useTranslation();
    const { configuration, invoiceDocument, contractDocument } = useApi();
    const { user, changePassword, sessionIdleTimeoutMinutes, applySessionIdleTimeoutMinutes } =
        useAuth();
    const { currentTheme, setTheme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(sessionIdleTimeoutMinutes);
    const [idleTimeoutSaving, setIdleTimeoutSaving] = useState(false);
    const [printersForm] = Form.useForm();
    const [documentsForm] = Form.useForm();
    const [modulesForm] = Form.useForm();
    const [contractsForm] = Form.useForm();
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [testEmailLoading, setTestEmailLoading] = useState(false);
    // Read-only: the `nrfactint` sequence both programs draw from, not a setting.
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('');
    const [nextInvoiceNumberUnavailable, setNextInvoiceNumberUnavailable] = useState(false);

    const [notificationSettings, setNotificationSettings] = useState({
        notifications: true,
        emailAlerts: false,
    });
    const [searchParams, setSearchParams] = useSearchParams();

    const [contractDocumentEngine, setContractDocumentEngine] =
        useState<ContractDocumentEngine>('word');
    const [contractDocumentAvailable, setContractDocumentAvailable] = useState<
        ContractDocumentEngine[]
    >(['word']);
    const [contractDocumentLoading, setContractDocumentLoading] = useState(false);
    const [contractDocumentEditorOpen, setContractDocumentEditorOpen] = useState(false);

    const [invoiceDocumentEngine, setInvoiceDocumentEngine] =
        useState<InvoiceDocumentEngine>('legacy');
    const [invoiceDocumentAvailable, setInvoiceDocumentAvailable] = useState<
        InvoiceDocumentEngine[]
    >(['legacy', 'factura_new', 'html']);
    const [invoiceDocumentLoading, setInvoiceDocumentLoading] = useState(false);
    const [invoiceDocumentEditorOpen, setInvoiceDocumentEditorOpen] = useState(false);
    const [invoiceTemplates, setInvoiceTemplates] = useState<InvoiceDocumentTemplatePayload[]>([]);

    // Load configurations on mount
    const loadConfigurations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await configuration.getAll();
            if (response.success && response.data) {
                // Populate forms with loaded data
                printersForm.setFieldsValue({
                    imp_contracte: response.data.printer_contracts ?? '',
                    imp_facturi: response.data.printer_invoices ?? '',
                    imp_pliant: response.data.printer_flyers ?? '',
                    imp_card: response.data.printer_cards ?? '',
                });

                documentsForm.setFieldsValue({
                    invoice_template_achitare: response.data.INVOICE_TEMPLATE_ACHITARE ?? '',
                    invoice_template_prelungire: response.data.INVOICE_TEMPLATE_PRELUNGIRE ?? '',
                    invoice_template_vanzare_marfa:
                        response.data.INVOICE_TEMPLATE_VANZARE_MARFA ?? '',
                    invoice_template_stornare: response.data.INVOICE_TEMPLATE_STORNARE ?? '',
                });

                // Delphi's own rows for the alerts (H-2); the till alert is per cash point and
                // has its own panel (CashPointAlertSettings).
                modulesForm.setFieldsValue({
                    raport_email1: response.data.raport_email1 ?? '',
                    send_email1: response.data.send_email1 === '1',
                    raport_email2: response.data.raport_email2 ?? '',
                    send_email2: response.data.send_email2 === '1',
                    smtp_host: response.data.module_smtp_host ?? '',
                    smtp_port: response.data.module_smtp_port ?? '587',
                    smtp_secure: response.data.module_smtp_secure === '1',
                    smtp_user: response.data.module_smtp_username ?? '',
                    smtp_pass: response.data.module_smtp_password ?? '',
                    smtp_from: response.data.module_smtp_from ?? '',
                    overpaid_active: response.data.overpaid_active === '1',
                    overpaid_amount: response.data.contract_supraplatit ?? '0',
                    overpaid_per_day: response.data.contract_supraplatit_1zi ?? '0',
                    // Delphi's own rows for the high-value contract report (BL-15).
                    high_value_report_email: response.data.adremail_raport_cvmare ?? '',
                    high_value_report_active: response.data.email_raport_cvmare === '1',
                });

                // Delphi's rows, which this tab writes (every setting follows Delphi, H-2); the
                // application's own rows only until Delphi's exist.
                contractsForm.setFieldsValue({
                    durata: parseDelphiSettingNumber(
                        response.data.durata ?? response.data.contract_duration_days,
                        30,
                    ),
                    comision: parseDelphiSettingNumber(
                        response.data.comision ?? response.data.contract_commission_percentage,
                        0,
                    ),
                    comision_ea: parseDelphiSettingNumber(
                        response.data.comision_ea ??
                            response.data.contract_commission_ea_percentage,
                        0,
                    ),
                    // Delphi's row, which the payment reads first (BL-08); the application's
                    // own row only until Delphi's exists, 3 as the payment's default.
                    nr_prelungiri:
                        response.data.nrPrelungiriPermise ??
                        response.data.contract_extensions_allowed ??
                        '3',
                    infinit_prelungiri: response.data.contract_unlimited_extensions === '1',
                    // The legacy names the nightly status refresh reads, shared with Delphi
                    // (business_rules.md §2); a missing row counts as 0 there too.
                    toleranta_mai_mare: response.data.toleranta_mai_mare_de30zile ?? '0',
                    toleranta_mai_mic: response.data.toleranta_mai_mic_de30zile ?? '0',
                    toleranta_contract: response.data.toleranta_contract ?? '0',
                    tva: Number.parseFloat(response.data.tva ?? '0.19') * 100,
                    large_threshold_ron: Number.parseFloat(
                        response.data.contract_large_threshold_ron ?? '25000',
                    ),
                    large_commission_per_day: parseDelphiSettingNumber(
                        response.data.comision_25000 ??
                            response.data.contract_large_commission_per_day,
                        0.081,
                    ),
                });

                setIdleTimeoutMinutes(
                    resolveSessionIdleTimeoutMinutes(
                        response.data[SESSION_IDLE_TIMEOUT_CONFIG_KEY],
                    ),
                );
            }
        } catch (error) {
            console.error('Error loading configurations:', error);
            message.error(t('configuration.loadError'));
        } finally {
            setLoading(false);
        }
    }, [configuration, printersForm, documentsForm, modulesForm, contractsForm, t]);

    useEffect(() => {
        void loadConfigurations();
    }, [loadConfigurations]);

    // Apart from the settings load, so a slow read of a display-only number never holds the
    // page's Save buttons in their loading state.
    useEffect(() => {
        let active = true;
        void configuration.getNextInvoiceNumber().then(nextInvoice => {
            if (active) {
                setNextInvoiceNumber(nextInvoice.data?.nextInvoiceNumber ?? '');
                setNextInvoiceNumberUnavailable(!nextInvoice.success);
            }
        });
        return () => {
            active = false;
        };
    }, [configuration]);

    const loadInvoiceDocumentSettings = useCallback(async () => {
        try {
            const response = await invoiceDocument.getSettings();
            if (response.success && response.data) {
                setInvoiceDocumentEngine(response.data.engine);
                setInvoiceDocumentAvailable(response.data.availableEngines);
            }
        } catch (error) {
            console.error('Error loading invoice document settings:', error);
        }
    }, [invoiceDocument]);

    const loadInvoiceTemplates = useCallback(async () => {
        try {
            const response = await invoiceDocument.listTemplates();
            if (response.success && response.data) {
                setInvoiceTemplates(response.data.templates);
            }
        } catch (error) {
            console.error('Error loading invoice templates:', error);
        }
    }, [invoiceDocument]);

    useEffect(() => {
        void loadInvoiceDocumentSettings();
        void loadInvoiceTemplates();
    }, [loadInvoiceDocumentSettings, loadInvoiceTemplates]);

    const loadContractDocumentSettings = useCallback(async () => {
        try {
            const response = await contractDocument.getSettings();
            if (response.success && response.data) {
                setContractDocumentEngine(response.data.engine);
                setContractDocumentAvailable(response.data.availableEngines);
            }
        } catch (error) {
            console.error('Error loading contract document settings:', error);
        }
    }, [contractDocument]);

    useEffect(() => {
        void loadContractDocumentSettings();
    }, [loadContractDocumentSettings]);

    const saveContractDocumentEngine = async (engine: ContractDocumentEngine) => {
        try {
            setContractDocumentLoading(true);
            const response = await contractDocument.updateSettings(engine);
            if (response.success && response.data) {
                setContractDocumentEngine(response.data.engine);
                message.success(t('configuration.saveSuccess'));
            } else {
                message.error(response.error ?? t('configuration.saveError'));
            }
        } catch (error) {
            console.error('Error saving contract document engine:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setContractDocumentLoading(false);
        }
    };

    const saveInvoiceDocumentEngine = async (engine: InvoiceDocumentEngine) => {
        try {
            setInvoiceDocumentLoading(true);
            const response = await invoiceDocument.updateSettings(engine);
            if (response.success && response.data) {
                setInvoiceDocumentEngine(response.data.engine);
                message.success(t('configuration.saveSuccess'));
            } else {
                message.error(response.error ?? t('configuration.saveError'));
            }
        } catch (error) {
            console.error('Error saving invoice document engine:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setInvoiceDocumentLoading(false);
        }
    };

    const savePrinters = async (values: FormValues) => {
        try {
            setLoading(true);
            const configurations = [
                { denumire: 'printer_contracts', valoare: String(values.imp_contracte ?? '') },
                { denumire: 'printer_invoices', valoare: String(values.imp_facturi ?? '') },
                { denumire: 'printer_flyers', valoare: String(values.imp_pliant ?? '') },
                { denumire: 'printer_cards', valoare: String(values.imp_card ?? '') },
            ];

            await configuration.updateBatch(configurations);
            message.success(t('configuration.saveSuccess'));
        } catch (error) {
            console.error('Error saving printer configurations:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setLoading(false);
        }
    };

    const saveDocuments = async (values: FormValues) => {
        try {
            setLoading(true);
            const configurations = [
                {
                    denumire: 'INVOICE_TEMPLATE_ACHITARE',
                    valoare: String(values.invoice_template_achitare ?? ''),
                },
                {
                    denumire: 'INVOICE_TEMPLATE_PRELUNGIRE',
                    valoare: String(values.invoice_template_prelungire ?? ''),
                },
                {
                    denumire: 'INVOICE_TEMPLATE_VANZARE_MARFA',
                    valoare: String(values.invoice_template_vanzare_marfa ?? ''),
                },
                {
                    denumire: 'INVOICE_TEMPLATE_STORNARE',
                    valoare: String(values.invoice_template_stornare ?? ''),
                },
            ];

            await configuration.updateBatch(configurations);
            message.success(t('configuration.saveSuccess'));
        } catch (error) {
            console.error('Error saving document configurations:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setLoading(false);
        }
    };

    const saveModules = async (values: FormValues) => {
        try {
            setLoading(true);
            const configurations = [
                { denumire: 'raport_email1', valoare: String(values.raport_email1 ?? '') },
                { denumire: 'send_email1', valoare: values.send_email1 ? '1' : '0' },
                { denumire: 'raport_email2', valoare: String(values.raport_email2 ?? '') },
                { denumire: 'send_email2', valoare: values.send_email2 ? '1' : '0' },
                { denumire: 'module_smtp_host', valoare: String(values.smtp_host ?? '') },
                { denumire: 'module_smtp_port', valoare: String(values.smtp_port ?? '587') },
                { denumire: 'module_smtp_secure', valoare: values.smtp_secure ? '1' : '0' },
                { denumire: 'module_smtp_username', valoare: String(values.smtp_user ?? '') },
                { denumire: 'module_smtp_password', valoare: String(values.smtp_pass ?? '') },
                { denumire: 'module_smtp_from', valoare: String(values.smtp_from ?? '') },
                { denumire: 'overpaid_active', valoare: values.overpaid_active ? '1' : '0' },
                {
                    denumire: 'contract_supraplatit',
                    valoare: String(values.overpaid_amount ?? '0'),
                },
                {
                    denumire: 'contract_supraplatit_1zi',
                    valoare: String(values.overpaid_per_day ?? '0'),
                },
                {
                    denumire: 'adremail_raport_cvmare',
                    valoare: String(values.high_value_report_email ?? ''),
                },
                {
                    denumire: 'email_raport_cvmare',
                    valoare: values.high_value_report_active ? '1' : '0',
                },
            ];

            await configuration.updateBatch(configurations);
            message.success(t('configuration.saveSuccess'));
        } catch (error) {
            console.error('Error saving module configurations:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setLoading(false);
        }
    };

    const sendTestEmail = async () => {
        try {
            setTestEmailLoading(true);
            const response = await configuration.sendSmtpTest(testEmailAddress);
            if (response.success) {
                message.success(t('configuration.modules.testEmailSuccess'));
            } else {
                message.error(response.error ?? t('configuration.modules.testEmailError'));
            }
        } catch (error) {
            console.error('Error sending test e-mail:', error);
            message.error(t('configuration.modules.testEmailError'));
        } finally {
            setTestEmailLoading(false);
        }
    };

    const saveContracts = async (values: FormValues) => {
        try {
            setLoading(true);
            const configurations = [
                { denumire: 'durata', valoare: toDelphiSettingNumber(values.durata, 30) },
                { denumire: 'comision', valoare: toDelphiSettingNumber(values.comision, 0) },
                {
                    denumire: 'comision_ea',
                    valoare: toDelphiSettingNumber(values.comision_ea, 0),
                },
                {
                    denumire: 'nrPrelungiriPermise',
                    valoare: String(values.nr_prelungiri ?? '3'),
                },
                {
                    denumire: 'toleranta_mai_mare_de30zile',
                    valoare: String(values.toleranta_mai_mare ?? '0'),
                },
                {
                    denumire: 'toleranta_mai_mic_de30zile',
                    valoare: String(values.toleranta_mai_mic ?? '0'),
                },
                {
                    denumire: 'toleranta_contract',
                    valoare: String(values.toleranta_contract ?? '0'),
                },
                {
                    denumire: 'tva',
                    valoare: String(Number.parseFloat(String(values.tva ?? '19')) / 100),
                },
                {
                    denumire: 'contract_large_threshold_ron',
                    valoare: String(values.large_threshold_ron ?? '25000'),
                },
                {
                    denumire: 'comision_25000',
                    valoare: toDelphiSettingNumber(values.large_commission_per_day, 0.081),
                },
            ];

            await configuration.updateBatch(configurations);
            message.success(t('configuration.saveSuccess'));
        } catch (error) {
            console.error('Error saving contract configurations:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (values: {
        currentPassword: string;
        newPassword: string;
    }) => {
        try {
            setLoading(true);
            const result = await changePassword(values.currentPassword, values.newPassword);
            if (result.success) {
                passwordForm.resetFields();
            }
        } catch (error) {
            console.error(error);
            message.error(t('settings.changePassword'));
        } finally {
            setLoading(false);
        }
    };

    const saveIdleTimeout = async (minutes: number) => {
        try {
            setIdleTimeoutSaving(true);
            const response = await configuration.updateBatch([
                { denumire: SESSION_IDLE_TIMEOUT_CONFIG_KEY, valoare: String(minutes) },
            ]);
            if (response.success) {
                setIdleTimeoutMinutes(minutes);
                // This workstation follows the new value at once; the others on their next heartbeat.
                applySessionIdleTimeoutMinutes(minutes);
                message.success(t('configuration.saveSuccess'));
            } else {
                message.error(response.error ?? t('configuration.saveError'));
            }
        } catch (error) {
            console.error('Error saving session idle timeout:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setIdleTimeoutSaving(false);
        }
    };

    const idleTimeoutLabel = (minutes: number) => {
        const { unit, count } = describeIdleTimeout(minutes);
        return t(`configuration.session.${unit}`, { count });
    };

    const handleSettingChange = (key: string, value: unknown) => {
        setNotificationSettings(prev => ({
            ...prev,
            [key]: value,
        }));
        message.success(t('configuration.saveSuccess'));
    };

    const items = [
        {
            key: 'profile',
            label: (
                <span>
                    <UserOutlined />
                    {t('configuration.tabs.profile')}
                </span>
            ),
            children: (
                <Card>
                    <Form
                        form={profileForm}
                        layout="vertical"
                        initialValues={{
                            username: user?.username,
                            email: user?.email,
                        }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="username"
                                    label={t('configuration.users.username')}
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="email" label={t('configuration.users.email')}>
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                    <Divider />
                    <Descriptions
                        data-testid="system-information"
                        title={t('settings.systemInformation')}
                        column={1}
                        size="small"
                        bordered
                    >
                        <Descriptions.Item label={t('settings.version')}>
                            {appVersion || t('settings.notAvailable')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.lastUpdated')}>
                            {lastProgramUpdate || t('settings.notAvailable')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.userRole')}>
                            {user?.role ?? t('settings.notAvailable')}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.loginTime')}>
                            {user?.lastLogin
                                ? new Date(user.lastLogin).toLocaleString()
                                : t('settings.notAvailable')}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            ),
        },
        {
            key: 'password',
            label: (
                <span>
                    <KeyOutlined />
                    {t('configuration.tabs.password')}
                </span>
            ),
            children: (
                <Card>
                    <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="currentPassword"
                                    label={t('settings.currentPassword')}
                                    rules={[
                                        {
                                            required: true,
                                            message: t('settings.currentPasswordRequired'),
                                        },
                                    ]}
                                >
                                    <Input.Password />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="newPassword"
                                    label={t('settings.newPassword')}
                                    rules={[
                                        {
                                            required: true,
                                            message: t('settings.newPasswordRequired'),
                                        },
                                        { min: 6, message: t('settings.passwordMinLength') },
                                    ]}
                                >
                                    <Input.Password />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="confirmPassword"
                                    label={t('settings.confirmPassword')}
                                    dependencies={['newPassword']}
                                    rules={[
                                        {
                                            required: true,
                                            message: t('settings.confirmPasswordRequired'),
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (
                                                    !value ||
                                                    getFieldValue('newPassword') === value
                                                ) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(
                                                    new Error(t('settings.passwordsDoNotMatch')),
                                                );
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {t('settings.changePassword')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            ),
        },
        {
            key: 'preferences',
            label: (
                <span>
                    <GlobalOutlined />
                    {t('configuration.tabs.preferences')}
                </span>
            ),
            children: (
                <Card>
                    <Row gutter={24}>
                        <Col span={12}>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ marginBottom: 8 }}>{t('settings.language')}</div>
                                <LanguageSelector />
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ marginBottom: 8 }}>{t('settings.theme')}</div>
                                <Select
                                    value={currentTheme}
                                    onChange={(value: ThemeName) => {
                                        setTheme(value);
                                        message.success(t('configuration.saveSuccess'));
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    {themeSelectOptions(t)}
                                </Select>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ marginBottom: 8 }} id="session-idle-timeout-label">
                                    {t('configuration.session.idleTimeout')}
                                </div>
                                <Select<number>
                                    aria-labelledby="session-idle-timeout-label"
                                    data-testid="session-idle-timeout"
                                    value={idleTimeoutMinutes}
                                    onChange={saveIdleTimeout}
                                    loading={idleTimeoutSaving}
                                    disabled={idleTimeoutSaving}
                                    style={{ width: '100%' }}
                                    options={idleTimeoutOptions(idleTimeoutMinutes).map(
                                        minutes => ({
                                            value: minutes,
                                            label: idleTimeoutLabel(minutes),
                                        }),
                                    )}
                                />
                                <Typography.Text
                                    type="secondary"
                                    style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                                >
                                    {t('configuration.session.idleTimeoutHelp')}
                                </Typography.Text>
                            </div>
                        </Col>
                    </Row>
                    <Divider />
                    <Row gutter={24}>
                        <Col span={12}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12,
                                }}
                            >
                                <span>
                                    <BellOutlined style={{ marginRight: 8 }} />
                                    {t('settings.enableNotifications')}
                                </span>
                                <Switch
                                    checked={notificationSettings.notifications}
                                    onChange={checked =>
                                        handleSettingChange('notifications', checked)
                                    }
                                />
                            </div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {t('settings.enableNotificationsHelp')}
                            </Typography.Text>
                        </Col>
                        <Col span={12}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12,
                                }}
                            >
                                <span>{t('settings.emailAlerts')}</span>
                                <Switch
                                    checked={notificationSettings.emailAlerts}
                                    onChange={checked =>
                                        handleSettingChange('emailAlerts', checked)
                                    }
                                />
                            </div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {t('settings.emailAlertsHelp')}
                            </Typography.Text>
                        </Col>
                    </Row>
                </Card>
            ),
        },
        {
            key: 'printers',
            label: (
                <span>
                    <PrinterOutlined />
                    {t('configuration.tabs.printers')}
                </span>
            ),
            children: (
                <Card>
                    <Form form={printersForm} layout="vertical" onFinish={savePrinters}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="imp_contracte"
                                    label={t('configuration.printers.contracts')}
                                >
                                    <Select placeholder={t('configuration.printers.selectPrinter')}>
                                        <Option value="">
                                            {t('configuration.printers.defaultPrinter')}
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="imp_facturi"
                                    label={t('configuration.printers.invoices')}
                                >
                                    <Select placeholder={t('configuration.printers.selectPrinter')}>
                                        <Option value="">
                                            {t('configuration.printers.defaultPrinter')}
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="imp_pliant"
                                    label={t('configuration.printers.flyers')}
                                >
                                    <Select placeholder={t('configuration.printers.selectPrinter')}>
                                        <Option value="">
                                            {t('configuration.printers.defaultPrinter')}
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="imp_card"
                                    label={t('configuration.printers.cards')}
                                >
                                    <Select placeholder={t('configuration.printers.selectPrinter')}>
                                        <Option value="">
                                            {t('configuration.printers.defaultPrinter')}
                                        </Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {t('configuration.save')}
                            </Button>
                        </Form.Item>
                    </Form>

                    {/*
                     * Below the shop-wide fields and saved separately, because the label
                     * printer is configured per cash point. It was previously a sixth
                     * dropdown in the form above, writing a key nothing read.
                     */}
                    <Divider />
                    <LabelPrinterSettings />
                    <Divider />
                    <ScaleTestCard />
                </Card>
            ),
        },
        // Admin-only: who has an account, which category it is in and what each category may
        // open decide what other people see. The backend refuses every change from anyone else.
        ...(user?.role === 'ADMIN'
            ? [
                  {
                      key: 'users',
                      label: (
                          <span>
                              <UserOutlined />
                              {t('configuration.tabs.users')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <UserManagement />
                          </Card>
                      ),
                  },
                  {
                      key: 'userCategories',
                      label: (
                          <span>
                              <SafetyOutlined />
                              {t('configuration.tabs.userCategories')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <UserCategoryPermissions />
                          </Card>
                      ),
                  },
              ]
            : []),
        // Admin-only: the list maps workstations to shops, which is estate topology
        // rather than anything an operator needs. The backend enforces this too.
        ...(user?.role === 'ADMIN'
            ? [
                  {
                      key: 'workstations',
                      label: (
                          <span>
                              <DesktopOutlined />
                              {t('configuration.tabs.workstations')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <HardwareInstallations />
                          </Card>
                      ),
                  },
                  // Switching, creating or renaming a station re-points a computer at a cash
                  // history, so it is an administrator's job, like the workstation list above.
                  // To be moved behind the elevated-privilege check once that exists.
                  {
                      key: 'stations',
                      label: (
                          <span>
                              <ShopOutlined />
                              {t('configuration.tabs.stations')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <CashPointStations />
                          </Card>
                      ),
                  },
                  // The assistant reads shop-wide figures and spends money on every question;
                  // its key, models and budget are an administrator's call. The API enforces it.
                  {
                      key: 'aiAssistant',
                      label: (
                          <span>
                              <RobotOutlined />
                              {t('configuration.tabs.aiAssistant')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <AiAssistantSettings />
                          </Card>
                      ),
                  },
                  // Switching a paid module on or off is an administrator's decision; the
                  // backend refuses the switch to anyone else.
                  {
                      key: 'licensedModules',
                      label: (
                          <span>
                              <AppstoreAddOutlined />
                              {t('configuration.tabs.licensedModules')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <ApplicationModules />
                          </Card>
                      ),
                  },
                  // The zone every date and time is recorded and scheduled in; the API refuses
                  // the change to anyone else.
                  {
                      key: 'timeZone',
                      label: (
                          <span>
                              <ClockCircleOutlined />
                              {t('configuration.tabs.timeZone')}
                          </span>
                      ),
                      children: (
                          <Card>
                              <BusinessTimeZoneSettings />
                          </Card>
                      ),
                  },
              ]
            : []),
        {
            key: 'expenses',
            label: (
                <span>
                    <DollarOutlined />
                    {t('configuration.tabs.expenses')}
                </span>
            ),
            children: (
                <Card>
                    <Title level={4}>{t('configuration.expenses.title')}</Title>
                    <Typography.Paragraph type="secondary">
                        {t('configuration.expenses.description')}
                    </Typography.Paragraph>
                </Card>
            ),
        },
        {
            key: 'metalPrices',
            label: (
                <span>
                    <GoldOutlined />
                    {t('configuration.tabs.metalPrices')}
                </span>
            ),
            children: <MetalPriceConfig />,
        },
        {
            key: 'documents',
            label: (
                <span>
                    <FileTextOutlined />
                    {t('configuration.tabs.documents')}
                </span>
            ),
            children: (
                <Card>
                    <Form form={documentsForm} layout="vertical" onFinish={saveDocuments}>
                        <Row gutter={16}>
                            {/* Read-only, as Delphi's Configurare shows them: one sequence
                                (`nrfactint`) numbers invoices and receipts alike. Not form
                                fields, so the Save below never writes them. */}
                            <Col span={12}>
                                <Form.Item
                                    label={t('configuration.documents.invoiceNumber')}
                                    tooltip={t('configuration.documents.nextNumberHelp')}
                                    validateStatus={
                                        nextInvoiceNumberUnavailable ? 'error' : undefined
                                    }
                                    help={
                                        nextInvoiceNumberUnavailable
                                            ? t('configuration.documents.nextNumberUnavailable')
                                            : undefined
                                    }
                                >
                                    <Input
                                        readOnly
                                        value={nextInvoiceNumber}
                                        data-testid="next-invoice-number"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('configuration.documents.receiptNumber')}
                                    tooltip={t('configuration.documents.nextNumberHelp')}
                                >
                                    <Input
                                        readOnly
                                        value={nextInvoiceNumber}
                                        data-testid="next-receipt-number"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="invoice_template_achitare"
                                    label={t('configuration.documents.templateAchitare')}
                                >
                                    <Select allowClear>
                                        {invoiceTemplates.map(t => (
                                            <Option key={t.id} value={String(t.id)}>
                                                {t.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="invoice_template_prelungire"
                                    label={t('configuration.documents.templatePrelungire')}
                                >
                                    <Select allowClear>
                                        {invoiceTemplates.map(t => (
                                            <Option key={t.id} value={String(t.id)}>
                                                {t.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="invoice_template_vanzare_marfa"
                                    label={t('configuration.documents.templateVanzareMarfa')}
                                >
                                    <Select allowClear>
                                        {invoiceTemplates.map(t => (
                                            <Option key={t.id} value={String(t.id)}>
                                                {t.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="invoice_template_stornare"
                                    label={t('configuration.documents.templateStornare')}
                                >
                                    <Select allowClear>
                                        {invoiceTemplates.map(t => (
                                            <Option key={t.id} value={String(t.id)}>
                                                {t.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                data-testid="documents-save"
                            >
                                {t('configuration.save')}
                            </Button>
                        </Form.Item>
                    </Form>
                    <Divider orientation="left">{t('configuration.invoiceDocument.title')}</Divider>
                    <Form layout="vertical">
                        <Form.Item
                            label={t('configuration.invoiceDocument.engineLabel')}
                            help={t('configuration.invoiceDocument.engineHelp')}
                        >
                            <Select<'legacy' | 'factura_new' | 'html'>
                                value={invoiceDocumentEngine}
                                onChange={saveInvoiceDocumentEngine}
                                loading={invoiceDocumentLoading}
                                style={{ maxWidth: 420 }}
                            >
                                <Option value="legacy">
                                    {t('configuration.invoiceDocument.engineLegacy')}
                                </Option>
                                <Option
                                    value="factura_new"
                                    disabled={!invoiceDocumentAvailable.includes('factura_new')}
                                >
                                    {t('configuration.invoiceDocument.engineFacturaNew')}
                                    {!invoiceDocumentAvailable.includes('factura_new')
                                        ? ` (${t('configuration.invoiceDocument.engineFacturaNewDisabled')})`
                                        : ''}
                                </Option>
                                <Option
                                    value="html"
                                    disabled={!invoiceDocumentAvailable.includes('html')}
                                >
                                    {t('configuration.invoiceDocument.engineHtml')}
                                </Option>
                            </Select>
                        </Form.Item>
                        <Form.Item>
                            <Button onClick={() => setInvoiceDocumentEditorOpen(true)}>
                                {t('configuration.invoiceDocument.editTemplate')}
                            </Button>
                        </Form.Item>
                    </Form>
                    <Divider orientation="left">
                        {t('configuration.contractDocument.title')}
                    </Divider>
                    <Form layout="vertical">
                        <Form.Item
                            label={t('configuration.contractDocument.engineLabel')}
                            help={t('configuration.contractDocument.engineHelp')}
                        >
                            <Select<'word' | 'html'>
                                value={contractDocumentEngine}
                                onChange={saveContractDocumentEngine}
                                loading={contractDocumentLoading}
                                style={{ maxWidth: 320 }}
                            >
                                <Option value="word">
                                    {t('configuration.contractDocument.engineWord')}
                                </Option>
                                <Option
                                    value="html"
                                    disabled={!contractDocumentAvailable.includes('html')}
                                >
                                    {t('configuration.contractDocument.engineHtml')}
                                    {!contractDocumentAvailable.includes('html')
                                        ? ` (${t('configuration.contractDocument.engineHtmlDisabled')})`
                                        : ''}
                                </Option>
                            </Select>
                        </Form.Item>
                        <Form.Item>
                            <Button onClick={() => setContractDocumentEditorOpen(true)}>
                                {t('configuration.contractDocument.editTemplate')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            ),
        },
        {
            key: 'loyalty',
            label: (
                <span>
                    <StarOutlined />
                    {t('configuration.tabs.loyalty')}
                </span>
            ),
            children: (
                <Card>
                    <LoyaltySettings />
                    <Divider />
                    <ZeroCommissionPromotionsSettings />
                </Card>
            ),
        },
        {
            key: 'modules',
            label: (
                <span>
                    <SettingOutlined />
                    {t('configuration.tabs.modules')}
                </span>
            ),
            children: (
                <Card>
                    <CashPointAlertSettings />
                    <Divider />
                    <Form form={modulesForm} layout="vertical" onFinish={saveModules}>
                        <Title level={5}>{t('configuration.modules.reportEmails')}</Title>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Row gutter={16}>
                                <Col span={18}>
                                    <Form.Item
                                        name="raport_email1"
                                        label={t('configuration.modules.email1')}
                                    >
                                        <Input
                                            type="email"
                                            placeholder={t('configuration.modules.enterEmail')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="send_email1"
                                        valuePropName="checked"
                                        style={{ marginTop: '30px' }}
                                    >
                                        <Checkbox>{t('configuration.modules.sendEmail')}</Checkbox>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={18}>
                                    <Form.Item
                                        name="raport_email2"
                                        label={t('configuration.modules.email2')}
                                    >
                                        <Input
                                            type="email"
                                            placeholder={t('configuration.modules.enterEmail')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="send_email2"
                                        valuePropName="checked"
                                        style={{ marginTop: '30px' }}
                                    >
                                        <Checkbox>{t('configuration.modules.sendEmail')}</Checkbox>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Space>

                        <Divider />
                        <Title level={5}>{t('configuration.modules.overpaidAlert')}</Title>
                        <Typography.Paragraph type="secondary">
                            {t('configuration.modules.overpaidShopWideHint')}
                        </Typography.Paragraph>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="overpaid_amount"
                                    label={t('configuration.modules.overpaidAmount')}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="overpaid_per_day"
                                    label={t('configuration.modules.overpaidPerDay')}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="overpaid_active" valuePropName="checked">
                            <Checkbox>{t('configuration.modules.active')}</Checkbox>
                        </Form.Item>

                        <Divider />
                        <Title level={5}>{t('configuration.modules.highValueReport')}</Title>
                        <Typography.Paragraph type="secondary">
                            {t('configuration.modules.highValueReportHint')}
                        </Typography.Paragraph>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="high_value_report_email"
                                    label={t('configuration.modules.highValueReportEmail')}
                                >
                                    <Input
                                        type="email"
                                        placeholder={t('configuration.modules.enterEmail')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="high_value_report_active" valuePropName="checked">
                            <Checkbox>{t('configuration.modules.highValueReportActive')}</Checkbox>
                        </Form.Item>

                        <Divider />
                        <Title level={5}>{t('configuration.modules.smtp')}</Title>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="smtp_host"
                                    label={t('configuration.modules.smtpHost')}
                                >
                                    <Input placeholder={t('configuration.modules.enterSmtpHost')} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name="smtp_port"
                                    label={t('configuration.modules.smtpPort')}
                                >
                                    <InputNumber style={{ width: '100%' }} min={1} max={65535} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name="smtp_secure"
                                    valuePropName="checked"
                                    label={t('configuration.modules.smtpSecure')}
                                >
                                    <Checkbox />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="smtp_from"
                                    label={t('configuration.modules.smtpFrom')}
                                >
                                    <Input
                                        type="email"
                                        placeholder={t('configuration.modules.enterSmtpFrom')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="smtp_user"
                                    label={t('configuration.modules.smtpUser')}
                                >
                                    <Input placeholder={t('configuration.modules.enterSmtpUser')} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="smtp_pass"
                                    label={t('configuration.modules.smtpPassword')}
                                >
                                    <Input.Password
                                        placeholder={t('configuration.modules.enterSmtpPassword')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Row gutter={16} align="bottom">
                                <Col span={12}>
                                    <Form.Item
                                        label={t('configuration.modules.testEmailAddress')}
                                        htmlFor="smtp-test-email-address"
                                    >
                                        <Input
                                            id="smtp-test-email-address"
                                            type="email"
                                            value={testEmailAddress}
                                            onChange={event =>
                                                setTestEmailAddress(event.target.value)
                                            }
                                            placeholder={t(
                                                'configuration.modules.enterTestEmailAddress',
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Button
                                        loading={testEmailLoading}
                                        onClick={sendTestEmail}
                                        disabled={!testEmailAddress}
                                    >
                                        {t('configuration.modules.sendTestEmail')}
                                    </Button>
                                </Col>
                            </Row>
                        </Space>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                data-testid="save-modules"
                            >
                                {t('configuration.save')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            ),
        },
        {
            key: 'contracts',
            label: (
                <span>
                    <FileProtectOutlined />
                    {t('configuration.tabs.contracts')}
                </span>
            ),
            children: (
                <Card>
                    <Form form={contractsForm} layout="vertical" onFinish={saveContracts}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="durata"
                                    label={t('configuration.contracts.duration')}
                                    rules={[
                                        {
                                            required: true,
                                            message: t('configuration.contracts.durationRequired'),
                                        },
                                    ]}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={1}
                                        addonAfter={t('configuration.contracts.days')}
                                        data-testid="contract-duration"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="comision"
                                    label={t('configuration.contracts.commission')}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        formatter={value => `${value}%`}
                                        parser={value => Number(value?.replace('%', '')) || 0}
                                        data-testid="contract-commission"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="comision_ea"
                                    label={t('configuration.contracts.commissionEa')}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        formatter={value => `${value}%`}
                                        parser={value => Number(value?.replace('%', '')) || 0}
                                        data-testid="contract-commission-ea"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="nr_prelungiri"
                                    label={t('configuration.contracts.extensionsAllowed')}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        data-testid="contract-extensions-allowed"
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name="toleranta_mai_mare"
                                    label={t('configuration.contracts.toleranceAbove30')}
                                    tooltip={t('configuration.contracts.toleranceHelp')}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={0}
                                        addonAfter={t('configuration.contracts.days')}
                                        data-testid="contract-tolerance-above-30"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="toleranta_mai_mic"
                                    label={t('configuration.contracts.toleranceBelow30')}
                                    tooltip={t('configuration.contracts.toleranceHelp')}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={0}
                                        addonAfter={t('configuration.contracts.days')}
                                        data-testid="contract-tolerance-below-30"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="toleranta_contract"
                                    label={t('configuration.contracts.toleranceGeneral')}
                                    tooltip={t('configuration.contracts.toleranceGeneralHelp')}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={0}
                                        addonAfter={t('configuration.contracts.days')}
                                        data-testid="contract-tolerance-general"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="tva" label={t('configuration.contracts.vatRate')}>
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        step={1}
                                        addonAfter="%"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="large_threshold_ron"
                                    label={t('configuration.contracts.largeContractThreshold')}
                                    tooltip={t(
                                        'configuration.contracts.largeContractThresholdHelp',
                                    )}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        step={500}
                                        addonAfter="RON"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="large_commission_per_day"
                                    label={t('configuration.contracts.largeContractCommission')}
                                    tooltip={t(
                                        'configuration.contracts.largeContractCommissionHelp',
                                    )}
                                >
                                    <InputNumber<number>
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={100}
                                        step={0.001}
                                        addonAfter="% / day"
                                        data-testid="contract-large-commission"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                data-testid="contracts-save"
                            >
                                {t('configuration.save')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            ),
        },
    ];

    const activeTab = resolveConfigurationTab(
        searchParams.get(CONFIGURATION_TAB_QUERY_PARAM),
        items.map(item => item.key),
    );

    return (
        <div>
            <PageHeader
                accent="#475569"
                icon={<SettingOutlined aria-hidden />}
                title={t('configuration.title')}
                subtitle={t('configuration.subtitle')}
            />
            <Tabs
                activeKey={activeTab}
                onChange={key =>
                    setSearchParams({ [CONFIGURATION_TAB_QUERY_PARAM]: key }, { replace: true })
                }
                items={items.map(item =>
                    // Antd keeps a visited pane mounted. The member counts of the roles change on the
                    // Users tab, so this pane is dropped when left and reads again when entered.
                    item.key === 'userCategories' && item.key !== activeTab
                        ? { ...item, children: null }
                        : item,
                )}
                type="card"
                size="large"
                tabPosition="left"
            />
            <ContractDocumentEditor
                open={contractDocumentEditorOpen}
                onClose={() => setContractDocumentEditorOpen(false)}
            />
            <InvoiceDocumentEditor
                open={invoiceDocumentEditorOpen}
                onClose={() => setInvoiceDocumentEditorOpen(false)}
            />
        </div>
    );
};

export default ConfigurePage;
